import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import PlayerImagePlaceholderMasked from '@/assets/images/playerImagePlaceholderMasked';
import { Text } from '@/components/TextComponent';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { playerPropsSelector, usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { UfcGradient } from '@/feature/fantasy-event-sheets/components/ufc-events/UfcGradient';
import { EventWidgetImageContainer } from '@/feature/player-profile-sheets/components/EventWidgetImageContainer';
import { designSystem } from '@/styles/styles';
import { IndividualVersusEvent, Player } from '@/types/api.generated';
import { MAX_WEB_WIDTH } from '@/utils/constants-platform-specific';
import { defaultZustandCompareFunction } from '@/utils/default-zustand-compare-function';
import { isPlayerImgPlaceholder } from '@/utils/player';

import { useSinglePlayerProfile } from '../../SinglePlayerProfileProvider';

type UfcFightRowProps = {
    eventInfo: IndividualVersusEvent;
};

export const UfcFightRow = ({ eventInfo }: UfcFightRowProps) => {
    const [leftPlayer, rightPlayer] = eventInfo.players;

    const listOfPlayersWithEvents: PlayerWithTeamAndEvent[] = useMemo(
        () =>
            eventInfo.players
                .map(player => {
                    return eventInfo
                        ? {
                              ...player,
                              event: eventInfo,
                              league: eventInfo.league,
                              sport: eventInfo.sport,
                          }
                        : null;
                })
                .filter(Boolean),
        [eventInfo]
    );
    const { openSinglePlayerProfile } = useSinglePlayerProfile();

    const openPressPlayer = useCallback(
        (player: Player) => {
            const playerWithEvent = listOfPlayersWithEvents.find(p => p.id === player.id);
            if (playerWithEvent) {
                openSinglePlayerProfile(playerWithEvent);
            }
        },
        [listOfPlayersWithEvents, openSinglePlayerProfile]
    );

    return (
        <Row
            marginHorizontal={'s16'}
            marginTop={'s12'}
            overflow={'hidden'}
            maxWidth={MAX_WEB_WIDTH}
            width={'100%'}
            alignSelf={'center'}
        >
            <UfcPlayerCard player={leftPlayer} alignment="left" openPressPlayer={() => openPressPlayer(leftPlayer)} />
            <VsDot />
            <UfcPlayerCard
                player={rightPlayer}
                alignment="right"
                openPressPlayer={() => openPressPlayer(rightPlayer)}
            />
        </Row>
    );
};

const UfcPlayerCard = ({
    player,
    alignment = 'left',
    openPressPlayer,
}: {
    player: Player;
    alignment?: 'left' | 'right';
    openPressPlayer: () => void;
}) => {
    const isLeftAlignment = alignment === 'left';

    const isUnavailable = player === undefined || player === null;

    const isPlaceholderImage = isPlayerImgPlaceholder(player?.icon);

    return (
        <TouchableBox flex={1} onPress={openPressPlayer} disabled={isUnavailable} opacity={isUnavailable ? 0.5 : 1}>
            <Box
                style={[styles.playerBox, isLeftAlignment ? styles.leftPlayerBox : styles.rightPlayerBox]}
                borderTopLeftRadius={'r16'}
                borderBottomLeftRadius={'r16'}
                borderTopRightRadius={'r2'}
                borderBottomRightRadius={'r2'}
                overflow={'hidden'}
                flexDirection={isLeftAlignment ? 'row' : 'row-reverse'}
                gap={'s6'}
            >
                <UfcGradient alignment={alignment} />

                {isPlaceholderImage ? (
                    <PlayerImagePlaceholderMasked id={player?.id} opacity={0.4} />
                ) : (
                    <EventWidgetImageContainer
                        height={44}
                        width={44}
                        shouldApplyMask
                        imageUrl={player?.icon}
                        maskOffset={7.7}
                    />
                )}
                <Box alignItems={isLeftAlignment ? 'flex-start' : 'flex-end'}>
                    <Text color={isUnavailable ? 'gray3' : 'white'}>{player?.lastName || 'Unavailable'}</Text>
                    {player ? <UfcProjectionDetails player={player} /> : <Text color="gray3">-</Text>}
                </Box>
            </Box>
        </TouchableBox>
    );
};

const PLAYER_HEIGHT = 68;
const BORDER_WIDTH = 1;
const BORDER_COLOR = '#1C1D21';

const UfcProjectionDetails = ({ player }: { player: Player }) => {
    const { t } = useTranslation('betslip_pickem');

    const { openMarketProjections: playerProjections } = usePlayerPropsStore(state => {
        return {
            allProjections: state.playerMarkets[player.id] ?? [],
            openMarketProjections: playerPropsSelector(player.id)(state),
        };
    }, defaultZustandCompareFunction);
    return (
        <Text variant="bodySmall" numberOfLines={1} lineHeight={20} letterSpacing={-0.08} color="gray3">
            {`${t('projection', { count: playerProjections?.length })}`}
        </Text>
    );
};

const styles = StyleSheet.create({
    playerBox: {
        height: PLAYER_HEIGHT,
        flex: 1,
        backgroundColor: designSystem.colors.neutral800,
        paddingVertical: 12 - BORDER_WIDTH,
        borderWidth: BORDER_WIDTH,
        borderStyle: 'solid',
        borderColor: BORDER_COLOR,
        paddingHorizontal: 8,
        overflow: 'hidden',
    },
    vsDot: {
        marginLeft: -12,
        borderRadius: 14,
        borderWidth: BORDER_WIDTH,
        borderColor: BORDER_COLOR,
        borderStyle: 'solid',
    },
    leftPlayerBox: {
        borderTopLeftRadius: 16,
        borderBottomLeftRadius: 16,
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
        flexDirection: 'row',
    },
    rightPlayerBox: {
        borderTopLeftRadius: 2,
        borderBottomLeftRadius: 2,
        borderTopRightRadius: 16,
        borderBottomRightRadius: 16,
        flexDirection: 'row-reverse',
    },
});
const VsDot = () => (
    <Box width={4} zIndex={1} alignContent={'center'} justifyContent={'center'}>
        <Box
            width={28}
            height={28}
            style={styles.vsDot}
            alignContent={'center'}
            justifyContent={'center'}
            backgroundColor={'gray8'}
        >
            <Box
                position={'absolute'}
                left={0}
                right={0}
                top={0}
                bottom={0}
                alignItems={'center'}
                justifyContent={'center'}
            >
                <Box width={4} height={36} backgroundColor={'gray8'} />
            </Box>
            <Text textAlign={'center'} variant={'labelLarge'} fontSize={13} letterSpacing={-0.08} color="gray3">
                vs
            </Text>
        </Box>
    </Box>
);
